export type PaymentMethod = "CASH" | "PAYPAY" | "CARD" | "BANK" | "OTHER";
export type CollectionMethod = Exclude<PaymentMethod, "OTHER">;
export type PaidTodayMethod = "UNPAID" | "CASH" | "OTHER";
export type ReceivableType = "TAB" | "TRANSFER_DUE";
export type SaveState = "idle" | "dirty" | "saving" | "saved" | "conflict" | "error";

export interface CustomerLine {
  lineId?: string;
  customerLabel: string;
  amount: number;
  memo?: string;
}

export interface ReceivableLine {
  lineId?: string;
  receivableId?: string;
  type: ReceivableType;
  customerLabel: string;
  amount: number;
  description?: string;
}

export interface ExpenseLine {
  lineId?: string;
  vendorName: string;
  amount: number;
  paymentMethod: PaymentMethod;
  memo?: string;
}

export interface ShiftLine {
  lineId?: string;
  staffId: string;
  staffName?: string;
  startedAt: string;
  endedAt: string;
  breakMinutes: number;
  baseHourlyRateSnapshot?: number;
  paidTodayMethod: PaidTodayMethod;
}

export interface CollectionLine {
  lineId?: string;
  collectionId?: string;
  receivableId: string;
  amount: number;
  receivedMethod: CollectionMethod;
  description?: string;
}

export interface PaymentBreakdown {
  paypay: number;
  card: number;
  tab: number;
  transferDue: number;
}

export interface DailyPayload {
  requestId: string;
  expectedRevision: number;
  businessDate: string;
  openingRegisterCash: number | null;
  closingRegisterCashActual: number | null;
  customers: CustomerLine[];
  payments: PaymentBreakdown;
  receivables: ReceivableLine[];
  expenses: ExpenseLine[];
  shifts: ShiftLine[];
  collections: CollectionLine[];
  note: string;
}

export interface ShiftCalculation {
  elapsedMinutes: number;
  normalMinutes: number;
  lateNightMinutes: number;
  baseWage: number;
  lateNightPremium: number;
  totalWage: number;
}

export interface DailyCalculation {
  salesTotal: number;
  nonCashSales: number;
  cashSales: number;
  expenseTotal: number;
  cashExpenseTotal: number;
  wageTotal: number;
  cashWagePaidTotal: number;
  cashCollectionTotal: number;
  dailyProfit: number;
  expectedRegisterCash: number | null;
  registerDifference: number | null;
  shifts: ShiftCalculation[];
}

export interface IntegrityIssue {
  code:
    | "PAYMENT_EXCEEDS_SALES"
    | "TAB_MISMATCH"
    | "TRANSFER_DUE_MISMATCH"
    | "SHIFT_INVALID"
    | "DAILY_LIMIT_EXCEEDED";
  message: string;
  field?: string;
}

export interface StaffConfig {
  staffId: string;
  staffName: string;
  hourlyRate: number;
  active: boolean;
}

export interface AppConfig {
  storeId: string;
  storeName: string;
  timezone: "Asia/Tokyo";
  defaultOpeningRegisterCash: number;
  lateNightPremiumRate: number;
  maxLineAmount: number;
  maxDailyAmount: number;
  registerDifferenceTolerance: number;
  wageRoundingMode: "ROUND" | "FLOOR" | "CEIL";
  staff: StaffConfig[];
  expensePaymentMethods: PaymentMethod[];
  collectionMethods: CollectionMethod[];
}

export interface DailyRecord extends DailyPayload {
  exists: true;
  recordId: string;
  revision: number;
  updatedAt: string;
  updatedBy: string;
  checksum: string;
  calculated: DailyCalculation;
}

export interface EmptyDailyRecord {
  exists: false;
  businessDate: string;
  revision: 0;
}

export type DailyResponse = DailyRecord | EmptyDailyRecord;

export interface SaveResponse {
  ok: true;
  businessDate: string;
  recordId: string;
  revision: number;
  checksum: string;
  calculated: DailyCalculation;
  savedAt: string;
  savedBy: string;
  data: DailyRecord;
  idempotent?: boolean;
}

export interface VoidDailyResponse {
  ok: true;
  businessDate: string;
  recordId: string;
  revision: number;
  status: "VOID";
  savedAt: string;
  savedBy: string;
}

export interface HistoryItem {
  businessDate: string;
  revision: number;
  salesTotal: number;
  cashSales: number;
  expenseTotal: number;
  wageTotal: number;
  dailyProfit: number;
  registerDifference: number | null;
  updatedAt: string;
  updatedBy: string;
  validationStatus: string;
}

export interface OpenReceivable {
  receivableId: string;
  businessDate: string;
  type: ReceivableType;
  customerLabel: string;
  originalAmount: number;
  collectedTotal: number;
  outstandingBalance: number;
  description: string;
}

export interface StaffMonthlyRow {
  staffId: string;
  staffName: string;
  normalMinutes: number;
  lateNightMinutes: number;
  totalMinutes: number;
  baseWage: number;
  lateNightPremium: number;
  totalWage: number;
}

export interface MonthlyResult {
  month: string;
  totals: {
    salesTotal: number;
    cashSales: number;
    paypaySales: number;
    cardSales: number;
    tabSales: number;
    transferDueSales: number;
    tabOutstanding: number;
    transferDueOutstanding: number;
    expenseTotal: number;
    wageTotal: number;
    dailyProfit: number;
    registerDifference: number;
  };
  staff: StaffMonthlyRow[];
  warnings: string[];
}

export interface ApiErrorShape {
  ok: false;
  code: string;
  message: string;
  details?: unknown;
}

export const DEFAULT_CONFIG: AppConfig = {
  storeId: "dnc-sendai",
  storeName: "Bar D&C",
  timezone: "Asia/Tokyo",
  defaultOpeningRegisterCash: 30_000,
  lateNightPremiumRate: 0.25,
  maxLineAmount: 1_000_000,
  maxDailyAmount: 10_000_000,
  registerDifferenceTolerance: 0,
  wageRoundingMode: "ROUND",
  staff: [
    { staffId: "staff-01", staffName: "スタッフA", hourlyRate: 1_100, active: true },
  ],
  expensePaymentMethods: ["CASH", "PAYPAY", "CARD", "BANK", "OTHER"],
  collectionMethods: ["CASH", "PAYPAY", "CARD", "BANK"],
};

export interface BootstrapData {
  config: AppConfig;
  daily: DailyResponse;
  receivables: OpenReceivable[];
}
